import React, { useEffect, useState, useMemo } from "react";
import { db } from "../firebaseConfig";
import { collection, onSnapshot, query, orderBy, doc, updateDoc } from "firebase/firestore";
import styled, { keyframes } from "styled-components";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import ClearIcon from "@mui/icons-material/Clear";
import { IconButton } from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupIcon from "@mui/icons-material/Group";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtGBP  = (n) => `£${parseFloat(n || 0).toFixed(2)}`;
const fmtDate = (str) => str ? new Date(str).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const initials = (name) => (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const REASON_COLORS = {
  INVOICED:  { bg: "#eff6ff", border: "#bfdbfe", color: "#2563eb" },
  PAID:      { bg: "#f0fdf4", border: "#86efac", color: "#059669" },
  CANCELLED: { bg: "#fef2f2", border: "#fca5a5", color: "#dc2626" },
  REFUND:    { bg: "#fffbeb", border: "#fcd34d", color: "#d97706" },
};

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <TooltipBox>
      <TooltipLabel>{label}</TooltipLabel>
      <TooltipValue>{fmtGBP(payload[0].value)}</TooltipValue>
    </TooltipBox>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SelectInput = ({ label, name, value, onChange, options, placeholder }) => (
  <Field>
    <label>{label}</label>
    <select name={name} value={value} onChange={onChange} className="custom-input">
      <option value="">{placeholder || `All`}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </Field>
);

const TextInput = ({ label, name, value, onChange, type = "text" }) => (
  <Field>
    <label>{label}</label>
    <input type={type} name={name} value={value} onChange={onChange} placeholder={label} className="custom-input" />
  </Field>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const InvoicedLedger = () => {
  const [rows,     setRows]     = useState([]);
  const [filters,  setFilters]  = useState({ folderNo: "", agent: "", reason: "", startDate: "", endDate: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editId,   setEditId]   = useState(null);
  const [form,     setForm]     = useState({});

  const reasons = ["INVOICED", "PAID", "CANCELLED", "REFUND"];

  useEffect(() => {
    const q = query(collection(db, "invoiced"), orderBy("timestamp", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const handleFilter = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });

  const filtered = rows.filter((r) => {
    const matchFolder = filters.folderNo ? String(r.folderNo || "").includes(filters.folderNo)                       : true;
    const matchAgent  = filters.agent    ? String(r.agent || "").toLowerCase() === filters.agent.toLowerCase()       : true;
    const matchReason = filters.reason   ? r.reason === filters.reason                                               : true;
    const matchStart  = filters.startDate ? r.date >= filters.startDate                                              : true;
    const matchEnd    = filters.endDate   ? r.date <= filters.endDate                                                : true;
    return matchFolder && matchAgent && matchReason && matchStart && matchEnd;
  });

  const totalAmount = filtered.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const agents = Array.from(new Set(rows.map((r) => r.agent?.toUpperCase()))).filter(Boolean);

  const normalizedRows = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      if (!r.agent) return;
      const key = r.agent.trim().toUpperCase();
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return map;
  }, [rows]);

  const months = useMemo(() => {
    const m = [];
    const today = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      m.push({ month: d.toLocaleString("default", { month: "short", year: "2-digit" }), key: `${d.getFullYear()}-${d.getMonth() + 1}` });
    }
    return m;
  }, []);

  const monthlyTotals = useMemo(() => months.map((m) => ({
    month: m.month,
    total: Math.round(filtered.filter((r) => {
      const [y, mo] = r.date.split("-").map(Number);
      return `${y}-${mo}` === m.key;
    }).reduce((sum, r) => sum + Number(r.amount || 0), 0) * 100) / 100,
  })), [filtered, months]);

  const agentTotals = useMemo(() => Object.keys(normalizedRows).map((a) => ({
    agent: a,
    total: normalizedRows[a].reduce((sum, r) => sum + Number(r.amount || 0), 0),
  })), [normalizedRows]);

  const editRow = (row) => { setEditId(row.id); setForm(row); setEditOpen(true); };
  const updateRow = async () => {
    if (!editId) return;
    await updateDoc(doc(db, "invoiced", editId), form);
    setEditOpen(false);
  };

  return (
    <Page>
      <PageTitle>Invoiced Ledger</PageTitle>

      {/* ── Filters ── */}
      <FilterCard>
        <FormGrid>
          <TextInput   label="Folder No"   name="folderNo"   value={filters.folderNo}   onChange={handleFilter} />
          <SelectInput label="Agent"       name="agent"      value={filters.agent}      onChange={handleFilter} options={agents} />
          <SelectInput label="Reason"      name="reason"     value={filters.reason}     onChange={handleFilter} options={reasons} />
          <TextInput   label="Start Date"  name="startDate"  value={filters.startDate}  onChange={handleFilter} type="date" />
          <TextInput   label="End Date"    name="endDate"    value={filters.endDate}    onChange={handleFilter} type="date" />
        </FormGrid>
      </FilterCard>

      {/* ── Summary Card ── */}
      <SummaryRow>
        <SummaryCard color="#2563eb" bg="#eff6ff" border="#bfdbfe">
          <CardIcon color="#2563eb"><ReceiptLongIcon style={{ fontSize: 22 }} /></CardIcon>
          <CardBody>
            <CardLabel>Total Invoiced</CardLabel>
            <CardValue color="#2563eb">{fmtGBP(totalAmount)}</CardValue>
            <CardSub>{filtered.length} entries</CardSub>
          </CardBody>
        </SummaryCard>

        <SummaryCard color="#059669" bg="#f0fdf4" border="#bbf7d0">
          <CardIcon color="#059669"><TrendingUpIcon style={{ fontSize: 22 }} /></CardIcon>
          <CardBody>
            <CardLabel>Avg Per Entry</CardLabel>
            <CardValue color="#059669">{filtered.length ? fmtGBP(totalAmount / filtered.length) : "£0.00"}</CardValue>
            <CardSub>across filtered results</CardSub>
          </CardBody>
        </SummaryCard>

        <SummaryCard color="#7c3aed" bg="#f5f3ff" border="#ddd6fe">
          <CardIcon color="#7c3aed"><GroupIcon style={{ fontSize: 22 }} /></CardIcon>
          <CardBody>
            <CardLabel>Unique Agents</CardLabel>
            <CardValue color="#7c3aed">{agents.length}</CardValue>
            <CardSub>in full dataset</CardSub>
          </CardBody>
        </SummaryCard>
      </SummaryRow>

      {/* ── Charts ── */}
      <ChartsRow>
        <ChartCard>
          <ChartTitle>Monthly Totals — Last 12 Months</ChartTitle>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthlyTotals} margin={{ top: 4, right: 8, left: 0, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} angle={-35} textAnchor="end" />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `£${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="total" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Agent Totals</ChartTitle>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={agentTotals} margin={{ top: 4, right: 8, left: 0, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="agent" tick={{ fontSize: 11, fill: "#94a3b8" }} angle={-35} textAnchor="end" />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `£${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="total" fill="#dc2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </ChartsRow>

      {/* ── Table ── */}
      <TableWrapper>
        <table className="ledger-table">
          <thead>
            <tr>
              {["#", "Agent", "Folder No", "Amount", "Date", "Reason", "Remarks", ""].map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, idx) => (
              <tr key={r.id} className={idx % 2 === 0 ? "even" : "odd"}>
                <td className="sr">{idx + 1}</td>
                <td>
                  <AgentCell>
                    <Avatar>{initials(r.agent)}</Avatar>
                    <span>{r.agent}</span>
                  </AgentCell>
                </td>
                <td
                  onClick={() => {
      const url = `/FolderLedger?FolderNumber=${String(r.folderNo)}`;
      window.open(url, "_blank");
    }}
    style={{ cursor: "pointer" }}><Chip>#{r.folderNo}</Chip></td>
                <td><strong style={{ color: "#2563eb", fontVariantNumeric: "tabular-nums" }}>{fmtGBP(r.amount)}</strong></td>
                <td className="date-cell">{fmtDate(r.date)}</td>
                <td><ReasonBadge reason={r.reason}>{r.reason || "—"}</ReasonBadge></td>
                <td className="remarks">{r.remarks || "—"}</td>
                <td className="edit-cell">
                  <button className="edit-btn" onClick={() => editRow(r)} title="Edit">
                    <ModeEditIcon style={{ fontSize: 16 }} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: "32px", color: "#94a3b8", fontSize: 13, fontStyle: "italic" }}>
                  No entries found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="table-footer">
          <span>Showing <strong>{filtered.length}</strong> entries</span>
          <span>Total: <strong className="total-value">{fmtGBP(totalAmount)}</strong></span>
        </div>
      </TableWrapper>

      {/* ── Edit Modal ── */}
      {editOpen && (
        <ModalOverlay onClick={() => setEditOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <span>Edit Entry</span>
              <IconButton onClick={() => setEditOpen(false)} size="small">
                <ClearIcon />
              </IconButton>
            </ModalHeader>
            <FormGrid>
              {["agent", "folderNo", "amount", "date", "reason", "remarks"].map((field) => (
                <Field key={field}>
                  <label>{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                  <input
                    type={field === "amount" ? "number" : field === "date" ? "date" : "text"}
                    value={form[field] || ""}
                    onChange={(e) => setForm({ ...form, [field]: field === "amount" ? Number(e.target.value) : e.target.value })}
                    placeholder={field}
                    className="custom-input"
                  />
                </Field>
              ))}
            </FormGrid>
            <button
              className="custom-button"
              style={{ width: "100%", marginTop: 16, padding: "12px" }}
              onClick={updateRow}
            >
              Update Entry
            </button>
          </ModalBox>
        </ModalOverlay>
      )}
    </Page>
  );
};

// ─── Atoms ────────────────────────────────────────────────────────────────────

const AgentCell = styled.div`display: flex; align-items: center; gap: 7px;`;
const Avatar = styled.div`
  width: 26px; height: 26px; border-radius: 50%;
  background: #dbeafe; color: #1e40af;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 600; flex-shrink: 0;
`;
const Chip = styled.span`
  display: inline-flex; align-items: center;
  background: #f3f4f6; border: 0.5px solid #d1d5db;
  border-radius: 4px; padding: 2px 7px;
  font-size: 12px; font-family: monospace; color: #374151;
`;
const ReasonBadge = styled.span`
  display: inline-flex; align-items: center;
  background: ${p => REASON_COLORS[p.reason]?.bg || "#f3f4f6"};
  border: 0.5px solid ${p => REASON_COLORS[p.reason]?.border || "#d1d5db"};
  color: ${p => REASON_COLORS[p.reason]?.color || "#374151"};
  border-radius: 4px; padding: 2px 9px;
  font-size: 11px; font-weight: 600; letter-spacing: 0.5px;
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity:0; transform: translateY(6px); } to { opacity:1; transform:none; }`;

const Page = styled.div`
  padding: 2rem;
  max-width: 1300px;
  margin: 25px auto 0;
  animation: ${fadeIn} 0.3s ease;
`;

const PageTitle = styled.h2`
  font-size: 28px; font-weight: 700; color: #2563eb;
  background: #e0f2fe; display: inline-block;
  padding: 8px 20px; border-radius: 8px;
  margin-bottom: 24px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08); letter-spacing: 0.5px;
`;

// ─── Filter Card ──────────────────────────────────────────────────────────────

const FilterCard = styled.div`
  background: white;
  border: 0.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);

  .custom-input {
    padding: 9px 12px; font-size: 14px;
    border: 2px solid #e2e8f0; border-radius: 6px;
    outline: none; width: 100%; box-sizing: border-box;
    background: white; color: #1e293b;
    transition: border-color 0.2s, box-shadow 0.2s;
    &:focus { border-color: #2563eb; box-shadow: 0 0 4px rgba(37,99,235,0.2); }
    &::placeholder { color: #94a3b8; font-style: italic; }
  }
`;

const FormGrid = styled.div`
  display: flex; flex-wrap: wrap; gap: 14px;
`;

const Field = styled.div`
  display: flex; flex-direction: column; gap: 5px;
  min-width: 150px; flex: 1;
  label {
    font-size: 12px; font-weight: 600; color: #64748b;
    text-transform: uppercase; letter-spacing: 0.4px;
  }
`;

// ─── Summary Cards ────────────────────────────────────────────────────────────

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px; margin-bottom: 24px;
`;

const SummaryCard = styled.div`
  background: ${p => p.bg};
  border: 1px solid ${p => p.border};
  border-radius: 12px; padding: 18px 20px;
  display: flex; align-items: flex-start; gap: 14px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0,0,0,0.08); }
`;
const CardIcon  = styled.div`
  width: 42px; height: 42px; border-radius: 10px;
  background: ${p => p.color}18; color: ${p => p.color};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;
const CardBody  = styled.div`display: flex; flex-direction: column; gap: 2px;`;
const CardLabel = styled.div`font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.6px; color: #64748b;`;
const CardValue = styled.div`font-size: 26px; font-weight: 700; color: ${p => p.color}; font-variant-numeric: tabular-nums;`;
const CardSub   = styled.div`font-size: 12px; color: #94a3b8;`;

// ─── Charts ───────────────────────────────────────────────────────────────────

const ChartsRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 24px;
`;
const ChartCard = styled.div`
  flex: 1; min-width: 300px;
  background: white; border: 0.5px solid #e2e8f0;
  border-radius: 12px; padding: 20px 20px 10px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
`;
const ChartTitle = styled.div`
  font-size: 12px; font-weight: 700; color: #64748b;
  text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 14px;
`;
const TooltipBox   = styled.div`background: #1e293b; border-radius: 6px; padding: 8px 12px;`;
const TooltipLabel = styled.div`font-size: 11px; color: #94a3b8; margin-bottom: 2px;`;
const TooltipValue = styled.div`font-size: 13px; font-weight: 700; color: white;`;

// ─── Table ────────────────────────────────────────────────────────────────────

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 10px;
  border: 0.5px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  margin-bottom: 12px;

  .ledger-table {
    width: 100%; border-collapse: collapse;
    font-size: 13px; min-width: 800px;

    thead tr {
      background: white; border-bottom: 1px solid #e2e8f0;
    }
    th {
      padding: 10px 12px; text-align: left;
      font-size: 11px; font-weight: 600;
      color: #94a3b8; letter-spacing: 0.6px;
      text-transform: uppercase; white-space: nowrap;
    }
    th:first-child { padding-left: 16px; }

    td {
      padding: 10px 12px; vertical-align: middle;
      border-bottom: 0.5px solid #f1f5f9;
    }
    td:first-child { padding-left: 16px; }

    tr.even td { background: #fafafa; }
    tr.odd  td { background: white; }
    tr:hover td { background: #eff6ff !important; transition: background 0.1s; }
    tr:last-child td { border-bottom: none; }

    .sr        { color: #cbd5e1; font-size: 11px; width: 36px; }
    .date-cell { color: #64748b; font-size: 12px; white-space: nowrap; }
    .remarks   { color: #94a3b8; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; }

    .edit-cell { text-align: center; width: 44px; }
    .edit-btn {
      display: inline-flex; align-items: center; justify-content: center;
      width: 28px; height: 28px; border-radius: 6px;
      border: 0.5px solid #e2e8f0; background: transparent;
      cursor: pointer; color: #94a3b8; transition: all 0.12s;
      &:hover { background: #fee2e2; color: #dc3545; border-color: #fca5a5; }
    }
  }

  .table-footer {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 16px; font-size: 13px; color: #94a3b8;
    border-top: 0.5px solid #e2e8f0; background: #fafafa;
    border-radius: 0 0 10px 10px;
    .total-value { color: #1e293b; font-size: 14px; }
  }
`;

// ─── Modal ────────────────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
`;
const ModalBox = styled.div`
  background: white; border-radius: 12px; padding: 28px;
  width: min(520px, 94vw); max-height: 90vh; overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);

  .custom-input {
    padding: 9px 12px; font-size: 14px;
    border: 2px solid #e2e8f0; border-radius: 6px;
    outline: none; width: 100%; box-sizing: border-box;
    background: white; color: #1e293b;
    transition: border-color 0.2s;
    &:focus { border-color: #2563eb; }
  }
  .custom-button {
    background: #2563eb; color: white;
    padding: 10px 20px; font-size: 15px; font-weight: 600;
    border: none; border-radius: 8px; cursor: pointer;
    transition: background 0.2s;
    &:hover { background: #1d4ed8; }
  }
`;
const ModalHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 20px;
  span { font-size: 18px; font-weight: 700; color: #1e293b; }
`;

export default InvoicedLedger;